import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../utils/api';
import { useToast, ToastHost } from '../../../components/portal/Toast';
import Pagination, { PAGE_SIZE } from '../../../components/portal/Pagination';
import {
  RiUserAddLine,
  RiSearchLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCloseLine,
  RiSaveLine,
  RiTeamLine,
  RiEyeLine,
  RiEyeOffLine,
  RiLockPasswordLine,
  RiUserLine,
  RiAtLine,
  RiMailLine,
  RiPhoneLine,
  RiAlarmWarningLine,
  RiMapPinLine,
  RiBriefcaseLine,
  RiShieldUserLine,
  RiInformationLine,
  RiCakeLine,
} from 'react-icons/ri';

// Format a date column (ISO string / YYYY-MM-DD) for display.
function fmtDOB(v) {
  if (!v) return '';
  const d = new Date(String(v).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

const EMPTY_FORM = {
  name: '', username: '', password: '', role: 'employee',
  phone: '', position: '', is_active: true, date_of_birth: '',
};

const POSITION_OPTIONS = [
  'Full Stack Developer',
  'Frontend Developer',
  'Backend Developer',
  'UI/UX Designer',
  'Mobile Developer',
  'DevOps Engineer',
  'QA Engineer',
  'Project Manager',
  'HR',
  'Sales',
  'Marketing',
  'AI Automation',
  'Intern',
];

// ── Modal wrapper ──────────────────────────────────────────────────────────────
function Modal({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-[#1F2937]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl w-full max-w-lg shadow-[0_24px_60px_rgba(15,23,42,0.15)]">
        <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-[#F3F4F6]">
          <div>
            <h3 className="text-[#1F2937] font-bold text-lg">{title}</h3>
            {subtitle && <p className="text-[#9CA3AF] text-sm mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#1F2937] cursor-pointer transition-colors p-1 rounded-lg hover:bg-[#F3F4F6]"
          >
            <RiCloseLine size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── User form ──────────────────────────────────────────────────────────────────
function UserForm({ initial, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, type = 'text', required = false, placeholder = '') => (
    <div>
      <label className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide" htmlFor={key}>
        {label} {required && <span className="text-red-500 normal-case tracking-normal">*</span>}
      </label>
      <input
        id={key}
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="input-field"
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {field('name', 'Full Name', 'text', true, 'John Doe')}
        {field('username', 'Username', 'text', true, 'john.doe')}
      </div>

      <div>
        <label
          className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide"
          htmlFor="password"
        >
          <span className="inline-flex items-center gap-1.5">
            <RiLockPasswordLine size={13} />
            {isEdit ? 'New Password' : 'Temporary Password'}
            {!isEdit && <span className="text-red-500 normal-case tracking-normal">*</span>}
          </span>
          {isEdit && (
            <span className="ml-2 text-[10px] normal-case tracking-normal text-[#9CA3AF] font-normal">
              (leave blank to keep current)
            </span>
          )}
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPwd ? 'text' : 'password'}
            required={!isEdit}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={isEdit ? 'Enter a new password to reset' : 'Min. 8 characters'}
            minLength={isEdit && !form.password ? undefined : 8}
            autoComplete="new-password"
            className="input-field pr-11"
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#0984e3] transition-colors cursor-pointer"
            aria-label={showPwd ? 'Hide password' : 'Show password'}
          >
            {showPwd ? <RiEyeLine size={17} /> : <RiEyeOffLine size={17} />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide" htmlFor="role">
          Role <span className="text-red-500 normal-case tracking-normal">*</span>
        </label>
        <select
          id="role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="input-field cursor-pointer"
        >
          <option value="employee">Employee</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {field('phone', 'Phone Number', 'tel', false, '+91 98765 43210')}
        <div>
          <label
            className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide"
            htmlFor="position"
          >
            Job Position
          </label>
          <select
            id="position"
            value={form.position || ''}
            onChange={(e) => setForm({ ...form, position: e.target.value })}
            className="input-field cursor-pointer"
          >
            <option value="">— Select position —</option>
            {POSITION_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {/* Preserve any pre-existing custom value not in the list */}
            {form.position && !POSITION_OPTIONS.includes(form.position) && (
              <option value={form.position}>{form.position} (custom)</option>
            )}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide"
            htmlFor="date_of_birth"
          >
            Date of Birth
            <span className="ml-2 text-[10px] normal-case tracking-normal text-[#9CA3AF] font-normal">
              (used for birthday emails)
            </span>
          </label>
          <input
            id="date_of_birth"
            type="date"
            value={(form.date_of_birth || '').slice(0, 10)}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
            className="input-field cursor-pointer"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-outline flex-1 justify-center">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary flex-1 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading
            ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
            : <RiSaveLine size={15} />}
          {isEdit ? 'Save Changes' : 'Create User'}
        </button>
      </div>
    </form>
  );
}

// ── Profile view ───────────────────────────────────────────────────────────────
function UserProfileView({ user, onClose, onEdit }) {
  const initialsSource = user?.name || user?.username || 'U';
  const initials = ((initialsSource.match(/[A-Za-z]+/g) || [initialsSource])
    .map((w) => w[0]).join('').toUpperCase().slice(0, 2)) || 'U';
  const isActive = user?.is_active !== false;

  return (
    <div className="space-y-5 -m-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-t-2xl">
        <div
          className="h-28 w-full relative"
          style={{ background: 'linear-gradient(120deg,#0984e3 0%,#00b0ff 40%,#7c3aed 80%,#d500f9 100%)' }}
        >
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
            backgroundImage:
              'radial-gradient(circle at 25% 30%,#fff 1px,transparent 1.5px),radial-gradient(circle at 75% 70%,#fff 1px,transparent 1.5px)',
            backgroundSize: '28px 28px',
          }} />
          <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/30 backdrop-blur-md flex items-center gap-1.5">
            <RiShieldUserLine size={11} />
            {user?.role || 'employee'}
          </span>
        </div>
        <div className="px-6 pb-5 -mt-12">
          <div className="relative w-20 h-20 mb-3">
            <div
              className="w-full h-full rounded-2xl border-4 border-white flex items-center justify-center text-white text-2xl font-bold shadow-[0_10px_24px_rgba(9,132,227,0.3)]"
              style={{ background: 'linear-gradient(135deg,#0984e3 0%,#7c3aed 60%,#d500f9 100%)' }}
            >
              {initials}
            </div>
            <span
              className={`absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full border-[3px] border-white ${
                isActive ? 'bg-emerald-500' : 'bg-gray-400'
              }`}
              title={isActive ? 'Active' : 'Inactive'}
            />
          </div>
          <h2 className="text-[#0F172A] text-lg font-bold tracking-tight truncate">{user?.name || '—'}</h2>
          <p className="text-[#64748B] text-sm mt-0.5 truncate">
            {user?.position || 'Position not set'}
            {user?.username && <span className="text-[#94A3B8]"> · @{user.username}</span>}
          </p>
          <span className={`inline-flex items-center gap-1.5 mt-2 text-[11px] px-2.5 py-1 rounded-full font-semibold ${
            isActive
              ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
              : 'text-gray-500 bg-gray-100 border border-gray-200'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Detail grid */}
      <div className="px-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ProfileRow icon={RiUserLine}           label="Full Name"        value={user?.name}              accent="#0984e3" />
        <ProfileRow icon={RiAtLine}             label="Username"         value={user?.username}          accent="#0984e3" />
        <ProfileRow icon={RiMailLine}           label="Email"            value={user?.email_id}          accent="#00b0ff" />
        <ProfileRow icon={RiPhoneLine}          label="Phone"            value={user?.phone}             accent="#10b981" />
        <ProfileRow icon={RiAlarmWarningLine}   label="Emergency Phone"  value={user?.emergency_contact} accent="#ef4444" />
        <ProfileRow icon={RiBriefcaseLine}      label="Position"         value={user?.position}          accent="#7c3aed" />
        <ProfileRow icon={RiCakeLine}           label="Date of Birth"    value={fmtDOB(user?.date_of_birth)} accent="#ec4899" />
        <ProfileRow icon={RiShieldUserLine}     label="Role"             value={user?.role}              accent="#d500f9" />
        <ProfileRow icon={RiInformationLine}    label="Status"           value={isActive ? 'Active' : 'Inactive'} accent={isActive ? '#10b981' : '#9CA3AF'} />
        <div className="sm:col-span-2">
          <ProfileRow icon={RiMapPinLine}       label="Address"          value={user?.address}           accent="#f59e0b" multiline />
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-2 border-t border-[#F3F4F6]">
        <button onClick={onClose} className="btn btn-outline" style={{ padding: '0.55rem 1rem', fontSize: '13px' }}>
          Close
        </button>
        <button onClick={onEdit} className="btn btn-primary flex items-center gap-1.5" style={{ padding: '0.55rem 1rem', fontSize: '13px' }}>
          <RiEditLine size={14} /> Edit
        </button>
      </div>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value, accent, multiline }) {
  const hasValue = value !== undefined && value !== null && String(value).trim() !== '';
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F8FAFC] border border-[#E5E7EB]">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}
      >
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</p>
        <p className={`text-sm font-semibold mt-0.5 ${hasValue ? 'text-[#0F172A]' : 'text-[#CBD5E1] italic font-medium'} ${multiline ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
          {hasValue ? value : 'Not set'}
        </p>
      </div>
    </div>
  );
}

// ── Delete confirm ─────────────────────────────────────────────────────────────
function DeleteConfirm({ user, onConfirm, onCancel, loading }) {
  return (
    <div className="text-center space-y-5">
      <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-2xl flex items-center justify-center mx-auto">
        <RiDeleteBinLine size={26} className="text-red-500" />
      </div>
      <div>
        <p className="text-[#1F2937] font-bold text-lg">Delete {user.name || user.username || user.email_id}?</p>
        <p className="text-[#6B7280] text-sm mt-1.5">
          This will permanently remove the user and all associated data. This action cannot be undone.
        </p>
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} className="btn btn-outline flex-1 justify-center">Cancel</button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 btn justify-center bg-red-500 hover:bg-red-600 text-white border-transparent disabled:opacity-60"
        >
          {loading && <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />}
          Delete User
        </button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { toast, showToast, clearToast } = useToast();

  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search by 300ms before firing the API call
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (debouncedSearch) params.search = debouncedSearch;
    api.get('/users', { params })
      .then((res) => {
        const d = res.data;
        const data = d?.users || d?.data || (Array.isArray(d) ? d : []);
        setUsers(Array.isArray(data) ? data : []);
        setTotal(d?.meta?.total ?? data.length);
      })
      .finally(() => setLoading(false));
  }, [page, debouncedSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Reset to page 1 when search/filter changes
  useEffect(() => { setPage(1); }, [debouncedSearch, filter]);


  // date_of_birth is a real DATE column — send null when blank, never ''.
  const normalizeUserPayload = (form) => {
    const p = { ...form };
    p.date_of_birth = (p.date_of_birth || '').trim() || null;
    return p;
  };

  const handleAdd = async (form) => {
    await api.post('/users', normalizeUserPayload(form));
    setModal(null);
    fetchUsers();
    showToast('User created successfully.', 'success');
  };

  const handleEdit = async (form) => {
    const id = selected._id || selected.id;
    const payload = normalizeUserPayload(form);
    if (!payload.password) delete payload.password;
    await api.put(`/users/${id}`, payload);
    setModal(null);
    setSelected(null);
    fetchUsers();
    showToast('User updated successfully.', 'success');
  };

  const handleDelete = async () => {
    const id = selected._id || selected.id;
    setDeleteLoading(true);
    try {
      await api.delete(`/users/${id}`);
      setModal(null);
      setSelected(null);
      fetchUsers();
      showToast('User deleted.', 'success');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Search is server-side; role filter remains client-side (no server param yet).
  const filtered = users.filter((u) => {
    return filter === 'all' || u.role === filter;
  });

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">

      <ToastHost toast={toast} clearToast={clearToast} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="label-meta mb-1">People</p>
          <h1 className="heading-md text-[#1F2937]">User Management</h1>
          <p className="text-[#6B7280] text-sm mt-1">
            {total} total user{total !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setModal('add')}
          className="btn btn-primary flex items-center gap-2"
          style={{ padding: '0.6rem 1.2rem', fontSize: '13px' }}
        >
          <RiUserAddLine size={15} />
          Add User
        </button>
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={17} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or username…"
            className="input-field pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          {['all', 'employee', 'admin'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 cursor-pointer capitalize border ${
                filter === f
                  ? 'text-white border-transparent shadow-[0_4px_12px_rgba(9,132,227,0.3)]'
                  : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[rgba(9,132,227,0.3)] hover:text-[#0984e3]'
              }`}
              style={filter === f ? { background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' } : {}}
            >
              {f === 'all' ? 'All Users' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">Position</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">Role</th>
                <th className="text-right px-6 py-3.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(5)].map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 bg-[#F3F4F6] rounded animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-16">
                    <RiTeamLine size={32} className="text-[#E5E7EB] mx-auto mb-3" />
                    <p className="text-[#9CA3AF] text-sm font-medium">
                      {search ? 'No users match your search.' : 'No users yet. Create your first team member.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u._id || u.id} className="hover:bg-[#FAFBFC] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #0984e3 0%, #d500f9 100%)' }}
                        >
                          {(u.name || u.username || u.email_id || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-[#1F2937] font-semibold">{u.name || '—'}</p>
                          <p className="text-[#9CA3AF] text-xs">{u.username || u.email_id || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#6B7280]">{u.position || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={u.role === 'admin' ? 'badge badge-purple' : 'badge'}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => { setSelected(u); setModal('view'); }}
                          className="p-2 text-[#9CA3AF] hover:text-[#7c3aed] hover:bg-[rgba(124,58,237,0.08)] rounded-xl transition-all cursor-pointer"
                          aria-label="View profile"
                          title="View full profile"
                        >
                          <RiEyeLine size={15} />
                        </button>
                        <button
                          onClick={() => { setSelected(u); setModal('edit'); }}
                          className="p-2 text-[#9CA3AF] hover:text-[#0984e3] hover:bg-[rgba(9,132,227,0.08)] rounded-xl transition-all cursor-pointer"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <RiEditLine size={15} />
                        </button>
                        {u.username?.toLowerCase() !== 'admin' && u.email_id?.toLowerCase() !== 'admin@nexcess.com' && (
                          <button
                            onClick={() => { setSelected(u); setModal('delete'); }}
                            className="p-2 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-xl transition-all cursor-pointer"
                            aria-label="Delete"
                            title="Delete"
                          >
                            <RiDeleteBinLine size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} onChange={setPage} />
      </div>

      {/* Modals */}
      {modal === 'add' && (
        <Modal title="Add New User" subtitle="Fill in the details to create a new team member" onClose={() => setModal(null)}>
          <UserForm initial={EMPTY_FORM} onSave={handleAdd} onCancel={() => setModal(null)} isEdit={false} />
        </Modal>
      )}

      {modal === 'edit' && selected && (
        <Modal title="Edit User" subtitle={`Updating ${selected.name || selected.username || selected.email_id}`} onClose={() => { setModal(null); setSelected(null); }}>
          <UserForm
            initial={{ ...EMPTY_FORM, ...selected, password: '' }}
            onSave={handleEdit}
            onCancel={() => { setModal(null); setSelected(null); }}
            isEdit
          />
        </Modal>
      )}

      {modal === 'delete' && selected && (
        <Modal title="Delete User" onClose={() => { setModal(null); setSelected(null); }}>
          <DeleteConfirm
            user={selected}
            onConfirm={handleDelete}
            onCancel={() => { setModal(null); setSelected(null); }}
            loading={deleteLoading}
          />
        </Modal>
      )}

      {modal === 'view' && selected && (
        <ProfileModal onClose={() => { setModal(null); setSelected(null); }}>
          <UserProfileView
            user={selected}
            onClose={() => { setModal(null); setSelected(null); }}
            onEdit={() => setModal('edit')}
          />
        </ProfileModal>
      )}
    </div>
  );
}

// ── Frameless modal for the profile view (it has its own gradient header) ──────
function ProfileModal({ onClose, children }) {
  return (
    <div className="fixed inset-0 bg-[#1F2937]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl w-full max-w-2xl shadow-[0_24px_60px_rgba(15,23,42,0.18)] overflow-hidden relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 cursor-pointer transition-colors flex items-center justify-center"
          aria-label="Close"
        >
          <RiCloseLine size={16} />
        </button>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
